'use client';

import React, { useEffect, useState } from 'react';

import type { Popup } from '@nutralab/api';

import { PromoModal, type PromoModalData } from '@/app/_modals/PromoModal';
import { useModal } from '@/app/_providers/ModalProvider';
import { getImagePath, getImageUrl } from '@/shared/utils';

const STORAGE_KEY = 'promo_popups_shown';

function popupKey(popup: Popup): string {
    return `${getImagePath(popup.image)}::${popup.link}`;
}

function readShownKeys(): Set<string> {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
    } catch {
        return new Set();
    }
}

function persistShownKeys(keys: Set<string>): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(keys)));
    } catch {
        return;
    }
}

interface PromoPopupProviderProps {
    popups: Popup[];
    children: React.ReactNode;
}

/** Shows the CMS promo popups one after another, each after its delay and only once per browser. */
export function PromoPopupProvider({ popups, children }: PromoPopupProviderProps): React.ReactElement {
    const { openModal } = useModal();

    const [queue, setQueue] = useState<Popup[]>(() => {
        const shownKeys = readShownKeys();
        return popups.filter((popup) => !shownKeys.has(popupKey(popup)));
    });

    useEffect(() => {
        const [next, ...rest] = queue;
        if (!next) {
            return;
        }

        const timeout = setTimeout(
            () => {
                const shownKeys = readShownKeys();
                shownKeys.add(popupKey(next));
                persistShownKeys(shownKeys);

                openModal<PromoModalData>({
                    ModalComponent: PromoModal,
                    data: {
                        image: getImageUrl(next.image),
                        link: next.link,
                        onDismiss: () => setQueue(rest),
                    },
                });
            },
            Math.max(0, next.delay_seconds) * 1000,
        );

        return () => clearTimeout(timeout);
    }, [queue, openModal]);

    return <>{children}</>;
}
